import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth.store'
import authService from '@/services/auth.service'

let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string | null) => void; reject: (error: any) => void }> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const forceLogout = async () => {
  const auth = useAuthStore()
  await auth.reset()
}

const ApiService = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
    // withCredentials: true removed: Architecture uses Stateless Bearer Tokens.
  })

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('token')
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      
      return config
    },
    (error) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

      // Handle 401 Unauthorized globally
      if (error.response && error.response.status === 401) {
        
        // Prevent infinite loops if the refresh endpoint itself returns 401
        if (originalRequest.url?.includes('/refresh')) {
          await forceLogout()
          return Promise.reject(error)
        }

        if (!originalRequest._retry) {
          
          // If a refresh is already in progress, queue subsequent requests
          if (isRefreshing) {
            return new Promise<string | null>((resolve, reject) => {
              failedQueue.push({ resolve, reject })
            })
              .then((token) => {
                if (token) {
                  originalRequest.headers.Authorization = `Bearer ${token}`
                }
                return instance(originalRequest)
              })
              .catch((err) => Promise.reject(err))
          }

          originalRequest._retry = true
          isRefreshing = true

          try {
            // Lazy load service to prevent circular dependency issues during initialization
            
            const { data } = await authService.refresh()
            
            // Strictly map to RefreshTokenResponse
            const payload = data as any
            const token = payload.data?.access_token || payload.token
            
            localStorage.setItem('token', token)
            instance.defaults.headers.common['Authorization'] = `Bearer ${token}`
            originalRequest.headers.Authorization = `Bearer ${token}`
            
            processQueue(null, token)
            return instance(originalRequest)
          } catch (refreshError) {
            processQueue(refreshError, null)
            await forceLogout()
            return Promise.reject(refreshError)
          } finally {
            isRefreshing = false
          }
        }
      }
      
      return Promise.reject(error)
    }
  )

  return instance
}

export default ApiService
